import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Clock, AlertTriangle, FileText, Users, Brain } from 'lucide-react';
import { workflowService } from '@/services/turfai/workflowService';

interface ValidationResults {
  overall_score: number;
  document_scores: { [key: string]: number };
  completeness_score: number;
  accuracy_score: number;
  consistency_score: number;
  extraction_data?: any;
  classification_data?: any;
}

interface GapAnalysis {
  missing_documents: string[];
  invalid_documents: string[];
  recommendations: string[];
}

interface ApprovalRecommendation {
  recommendation: 'auto_approve' | 'manual_review' | 'request_missing_documents' | 'reject';
  overall_score: number;
  confidence_score: number;
  completeness_score: number;
  recommendation_reason: string;
  requires_action: boolean;
  action_items: string[];
}

interface BatchProcessingDashboardProps {
  executionId: string;
  onComplete?: (results: any) => void;
}

export const BatchProcessingDashboard: React.FC<BatchProcessingDashboardProps> = ({
  executionId,
  onComplete
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [executionStatus, setExecutionStatus] = useState<string>('queued');
  const [progress, setProgress] = useState(0);
  const [validationResults, setValidationResults] = useState<ValidationResults | null>(null);
  const [gapAnalysis, setGapAnalysis] = useState<GapAnalysis | null>(null);
  const [approvalRecommendation, setApprovalRecommendation] = useState<ApprovalRecommendation | null>(null);
  const [documentResults, setDocumentResults] = useState<any[]>([]);

  useEffect(() => {
    if (executionId) {
      pollExecutionStatus();
    }
  }, [executionId]);

  const pollExecutionStatus = async (retryCount = 0) => {
    try {
      console.log('📊 Polling execution status:', executionId);
      const execution = await workflowService.getExecutionStatus(executionId);
      console.log('Execution response:', execution);
      const status = execution.status || execution.attributes?.status || 'unknown';
      
      setExecutionStatus(status);
      
      // Update progress based on status
      const progressMap: { [key: string]: number } = {
        'queued': 10,
        'running': 50,
        'awaiting_user_input': 75,
        'completed': 100,
        'failed': 0
      };
      setProgress(progressMap[status] || 0);

      if (status === 'completed') {
        console.log('✅ Workflow completed, fetching results...');
        try {
          const outputs = execution.outputs || execution.attributes?.outputs;
          console.log('Outputs found:', outputs);
          console.log('Outputs keys:', outputs ? Object.keys(outputs) : 'no outputs');
          if (outputs) {
            // Load validation report
            console.log('Fetching validation report...');
            const report = await workflowService.getValidationReport(executionId);
            console.log('Validation report received:', report);
            console.log('Validation results:', report.validation_results);
            setValidationResults(report.validation_results);
            setGapAnalysis(report.gap_analysis);
            setApprovalRecommendation(report.approval_recommendation);
            
            // Debug: Check if state is being set
            console.log('State set - validationResults should be:', report.validation_results);
            setTimeout(() => {
              console.log('After timeout - current validation results state:', report.validation_results);
            }, 100);
            
            // Load document results from report (not from outputs)
            if (report.document_results) {
              console.log('Setting document results:', report.document_results);
              setDocumentResults(report.document_results);
            }
            
            onComplete?.(report);
          } else {
            console.warn('⚠️ Workflow completed but no outputs found');
          }
          
          setLoading(false);
          
        } catch (reportError) {
          console.error('Error fetching validation report:', reportError);
          setError(`Workflow completed but failed to fetch results: ${reportError instanceof Error ? reportError.message : 'Unknown error'}`);
          setLoading(false);
        }
      } else if (status === 'failed') {
        const errorMsg = execution.error || execution.attributes?.error || 'Workflow execution failed';
        console.error('❌ Workflow failed:', errorMsg);
        setError(errorMsg);
        setLoading(false);
      } else if (status === 'cancelled') {
        setError('Workflow execution was cancelled');
        setLoading(false);
      } else if (status === 'running' || status === 'queued') {
        // Continue polling
        console.log(`⏳ Workflow ${status}, continuing to poll...`);
        setTimeout(() => pollExecutionStatus(0), 2000); // Reset retry count on successful response
      } else {
        console.warn('Unknown execution status:', status);
        setTimeout(() => pollExecutionStatus(0), 2000);
      }
    } catch (err) {
      console.error('Error polling execution status:', err);
      
      const errorMsg = err instanceof Error ? err.message : 'Failed to check execution status';
      
      // Check for 403 permission errors - suggests execution belongs to different user/session
      if (errorMsg.includes('403') || errorMsg.includes('ForbiddenError') || errorMsg.includes('access your own workflow')) {
        setError(`🔒 Permission Error: This workflow execution may belong to a different session. Please restart the upload process.\n\nTechnical Details: ${errorMsg}`);
        setLoading(false);
        return;
      }
      
      // Implement retry logic for temporary network issues
      const maxRetries = 3;
      if (retryCount < maxRetries) {
        console.log(`Retrying status poll (${retryCount + 1}/${maxRetries}) in 5 seconds...`);
        setTimeout(() => pollExecutionStatus(retryCount + 1), 5000);
      } else {
        setError(`${errorMsg} (after ${maxRetries} retries). Please check if TurfAI services are running.`);
        setLoading(false);
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'running':
        return <Clock className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getApprovalBadge = (recommendation: string) => {
    const config = {
      'auto_approve': { variant: 'default' as const, color: 'bg-green-100 text-green-800', label: 'Auto Approved' },
      'manual_review': { variant: 'secondary' as const, color: 'bg-yellow-100 text-yellow-800', label: 'Manual Review' },
      'request_missing_documents': { variant: 'destructive' as const, color: 'bg-orange-100 text-orange-800', label: 'Missing Documents' },
      'reject': { variant: 'destructive' as const, color: 'bg-red-100 text-red-800', label: 'Rejected' }
    };
    
    const { variant, label } = config[recommendation as keyof typeof config] || config.manual_review;
    return <Badge variant={variant}>{label}</Badge>;
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {getStatusIcon(executionStatus)}
            Processing Documents...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Progress value={progress} className="w-full" />
            <p className="text-sm text-gray-600">
              Status: <span className="capitalize font-medium">{executionStatus}</span>
            </p>
            <p className="text-sm text-gray-500">
              Please wait while we classify and extract data from your documents...
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="whitespace-pre-line">{error}</AlertDescription>
          </Alert>
          <div className="flex gap-2 mt-4">
            <Button 
              onClick={() => {
                setError(null);
                setLoading(true);
                pollExecutionStatus(0);
              }} 
              variant="outline"
            >
              Retry Status Check
            </Button>
            <Button 
              onClick={() => window.location.reload()} 
              variant="secondary"
            >
              Restart Process
            </Button>
          </div>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium mb-2">Troubleshooting:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Ensure TurfAI services are running</li>
              <li>• Check your internet connection</li>
              <li>• Try the "TurfAI Connection Test" from the main menu</li>
              <li>• Check browser console for detailed error logs</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6">
      {/* Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {getStatusIcon(executionStatus)}
            Document Processing Complete
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Visual Completeness Indicator */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              <svg className="w-32 h-32 transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="none"
                  className="text-gray-200"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 56}`}
                  strokeDashoffset={`${2 * Math.PI * 56 * (1 - (validationResults?.completeness_score || 0))}`}
                  className={`transition-all duration-1000 ${
                    (validationResults?.completeness_score || 0) >= 0.9 ? 'text-green-500' :
                    (validationResults?.completeness_score || 0) >= 0.7 ? 'text-yellow-500' :
                    'text-red-500'
                  }`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold">
                    {Math.round((validationResults?.completeness_score || 0) * 100)}%
                  </div>
                  <div className="text-xs text-gray-600">Complete</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {documentResults.length}
              </div>
              <div className="text-sm text-gray-600">Documents Processed</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${getConfidenceColor(validationResults?.overall_score || 0)}`}>
                {Math.round((validationResults?.overall_score || 0) * 100)}%
              </div>
              <div className="text-sm text-gray-600">Overall Confidence</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${getConfidenceColor(validationResults?.accuracy_score || 0)}`}>
                {Math.round((validationResults?.accuracy_score || 0) * 100)}%
              </div>
              <div className="text-sm text-gray-600">Accuracy Score</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Extracted Data */}
      {validationResults?.extraction_data && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Extracted Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(validationResults.extraction_data).map(([key, value]) => (
                value && (
                  <div key={key} className="space-y-1">
                    <div className="text-sm font-medium text-gray-600 capitalize">
                      {key.replace(/_/g, ' ')}
                    </div>
                    <div className="text-sm bg-gray-50 p-2 rounded border">
                      {String(value)}
                    </div>
                  </div>
                )
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Classification Results */}
      {validationResults?.classification_data && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5" />
              Document Classification
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <div className="text-sm font-medium text-gray-600">Document Type</div>
                <div className="text-lg font-semibold">{validationResults.classification_data.classification}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600">Confidence</div>
                <div className="text-lg font-semibold text-green-600">
                  {Math.round((validationResults.classification_data.confidence || 0) * 100)}%
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600">Reasoning</div>
                <div className="text-sm text-gray-700">{validationResults.classification_data.reasoning}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approval Recommendation */}
      {approvalRecommendation && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Approval Recommendation</span>
              {getApprovalBadge(approvalRecommendation.recommendation)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-gray-700">{approvalRecommendation.recommendation_reason}</p>
              
              {approvalRecommendation.action_items.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Action Items:</h4>
                  <ul className="list-disc list-inside space-y-1">
                    {approvalRecommendation.action_items.map((item, index) => (
                      <li key={index} className="text-sm text-gray-600">{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gap Analysis */}
      {gapAnalysis && (gapAnalysis.missing_documents.length > 0 || gapAnalysis.invalid_documents.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Gap Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {gapAnalysis.missing_documents.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-700 mb-2">Missing Documents:</h4>
                  <div className="flex flex-wrap gap-2">
                    {gapAnalysis.missing_documents.map((doc, index) => (
                      <Badge key={index} variant="destructive">{doc}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {gapAnalysis.invalid_documents.length > 0 && (
                <div>
                  <h4 className="font-medium text-orange-700 mb-2">Invalid Documents:</h4>
                  <div className="flex flex-wrap gap-2">
                    {gapAnalysis.invalid_documents.map((doc, index) => (
                      <Badge key={index} variant="secondary">{doc}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {gapAnalysis.recommendations.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Recommendations:</h4>
                  <ul className="list-disc list-inside space-y-1">
                    {gapAnalysis.recommendations.map((rec, index) => (
                      <li key={index} className="text-sm text-gray-600">{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document Results with Individual Status */}
      {documentResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Document Classification Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {documentResults.map((result, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">{result.document_name || `Document ${index + 1}`}</h4>
                    <div className="flex items-center gap-2">
                      {result.success ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                      <Badge variant="outline">
                        {result.classification?.document_type || 'Unknown'}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Confidence:</span>
                      <span className={`ml-2 font-medium ${getConfidenceColor(result.confidence || 0)}`}>
                        {Math.round((result.confidence || 0) * 100)}%
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <span className={`ml-2 font-medium ${result.status === 'completed' ? 'text-green-600' : 'text-yellow-600'}`}>
                        {result.status || 'Processing'}
                      </span>
                    </div>
                  </div>

                  {/* Show validation score if available */}
                  {validationResults?.document_scores?.[result.document_name] && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-sm text-gray-500">Validation Score:</span>
                      <Progress 
                        value={validationResults.document_scores[result.document_name] * 100} 
                        className="h-2 flex-1"
                      />
                      <span className="text-sm font-medium">
                        {Math.round(validationResults.document_scores[result.document_name] * 100)}%
                      </span>
                    </div>
                  )}

                  {result.error && (
                    <Alert variant="destructive" className="mt-2">
                      <AlertDescription className="text-sm">{result.error}</AlertDescription>
                    </Alert>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex justify-center gap-4">
        {approvalRecommendation?.recommendation === 'auto_approve' && (
          <Button size="lg" className="bg-green-600 hover:bg-green-700">
            <CheckCircle className="h-4 w-4 mr-2" />
            Approve Onboarding
          </Button>
        )}
        
        {approvalRecommendation?.requires_action && (
          <Button size="lg" variant="outline">
            <Users className="h-4 w-4 mr-2" />
            Send for Manual Review
          </Button>
        )}
        
        <Button size="lg" variant="outline">
          <FileText className="h-4 w-4 mr-2" />
          Download Report
        </Button>
      </div>
    </div>
  );
};